import { Box, InputLabel, type SxProps, type Theme, Typography } from '@mui/material';

interface Props {
    label: string;
    value: string | number | null | undefined;
    fullWidth?: boolean;
    sx?: SxProps<Theme>;
}

/**
 * A read-only text field component that mimics the appearance of a TextField
 * but without any interactive behavior (no hover, focus, or edit capabilities).
 *
 * Use this instead of a disabled TextField when you want the content to appear
 * clearly visible (not greyed out) but not editable.
 */
export default function ReadOnlyTextField({ label, value, fullWidth = false, sx }: Props) {
    return (
        <Box
            sx={{
                position: 'relative',
                width: fullWidth ? '100%' : 'auto',
                ...sx,
            }}
        >
            <InputLabel
                shrink
                sx={{
                    position: 'absolute',
                    top: -2,
                    // Where a small outlined field's shrunk label sits, as it has no field
                    transform: 'translate(14px, -6px) scale(0.75)',
                    px: 0.5,
                    backgroundColor: 'neutral.white',
                    color: 'neutral.gray',
                    zIndex: 1,
                }}
            >
                {label}
            </InputLabel>
            <Box
                sx={{
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    px: '14px',
                    border: '1px solid',
                    borderColor: 'neutral.borderColor',
                    borderRadius: '4px',
                    backgroundColor: 'neautral.white',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: 'neutral.gray',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {value ?? ''}
                </Typography>
            </Box>
        </Box>
    );
}
